import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight, LogIn, Calendar } from "lucide-react";
import { SITE } from "@/lib/site";
import ClientLoginModal from "./ClientLoginModal";
import BookMeetingModal from "./BookMeetingModal";

const NAV_ITEMS = [
  { label: "Home", href: "#home" },
  { label: "About Us", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Tools", href: "#tools" },
  { label: "Why Axomind", href: "#why" },
  { label: "Resources", href: "#resources" },
  { label: "Blogs", href: "#blogs" },
  { label: "FAQs", href: "#faqs" },
  { label: "Contact", href: "#contact" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Listen for global "open-book-meeting" custom event
  useEffect(() => {
    const open = () => setBookOpen(true);
    window.addEventListener("open-book-meeting", open);
    return () => window.removeEventListener("open-book-meeting", open);
  }, []);

  const handleAnchor = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        data-testid="site-navigation"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 2xl:px-10 flex items-center justify-between h-44 lg:h-56">
          <button
            onClick={() => handleAnchor("#home")}
            data-testid="logo-button"
            className="flex items-center gap-3 group shrink-0"
            aria-label="Axomind home"
          >
            <img
              src={SITE.logoUrl}
              alt="Axomind Asset Services LLP logo"
              className="w-44 sm:w-48 lg:w-56 xl:w-36 2xl:w-52 h-auto object-contain"
              loading="eager"
            />
            <div
              className="hidden sm:flex flex-col items-start whitespace-nowrap"
              data-testid="brand-wordmark"
            >
              <div className="brand-wordmark text-white text-xl lg:text-2xl xl:text-lg 2xl:text-2xl">
                AXOMIND
              </div>
              <div className="brand-rule w-full mt-1 mb-1" />
              <div className="brand-subtitle text-[#D4AF37] text-[0.55rem] lg:text-[0.6rem]">
                Asset&nbsp;Services&nbsp;LLP
              </div>
              <div className="text-[12px] leading-snug text-gray-400 mt-1" data-testid="brand-amfi-tagline">
                AMFI Registered Mutual Fund Distributor | ARN-365843
              </div>
            </div>
          </button>

          <nav className="hidden xl:flex items-center gap-0 ml-6 2xl:ml-10">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.label}
                onClick={() => handleAnchor(item.href)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-1 2xl:px-2.5 py-2 text-xs 2xl:text-sm text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 relative group whitespace-nowrap"
              >
                {item.label}
                <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#D4AF37] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2 whitespace-nowrap shrink-0">
            <button
              onClick={() => setLoginOpen(true)}
              data-testid="nav-login-button"
              className="inline-flex items-center gap-1.5 px-2 2xl:px-3 py-2 rounded-full text-xs 2xl:text-sm text-gray-300 hover:text-[#D4AF37] transition whitespace-nowrap"
            >
              <LogIn size={14} /> Login
            </button>
            <button
              onClick={() => setBookOpen(true)}
              data-testid="nav-cta-book"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-black font-semibold text-sm hover:bg-[#FFD700] transition shadow-[0_8px_30px_-8px_rgba(212,175,55,0.6)] whitespace-nowrap"
            >
              <Calendar size={14} /> Book Meeting
              <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="xl:hidden w-11 h-11 rounded-lg glass flex items-center justify-center text-white"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="xl:hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-6 flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleAnchor(item.href)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-left py-3 px-3 rounded-lg text-gray-200 hover:text-[#D4AF37] hover:bg-white/5 transition"
                  >
                    {item.label}
                  </button>
                ))}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    onClick={() => { setOpen(false); setLoginOpen(true); }}
                    data-testid="mobile-login-button"
                    className="px-4 py-3 rounded-full bg-white/5 border border-white/10 text-gray-200 text-sm font-medium"
                  >
                    Client Login
                  </button>
                  <button
                    onClick={() => { setOpen(false); setBookOpen(true); }}
                    data-testid="mobile-book-button"
                    className="px-4 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold"
                  >
                    Book Meeting
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <ClientLoginModal open={loginOpen} onOpenChange={setLoginOpen} />
      <BookMeetingModal open={bookOpen} onOpenChange={setBookOpen} />
    </>
  );
}
